/**
 * Keyshare Backup Manager for iframe context
 *
 * Handles encrypted backup of MPC keyshares with support for:
 * - Server backup (ShareVault API)
 * - Cloud backup (Google Drive, etc.)
 * - Local file download
 *
 * All backups are encrypted with AES-256-GCM using either:
 * - Passkey-derived password (secure, requires device)
 * - Custom password (user-provided)
 */

// Inlined to avoid pulling UI dependencies (lucide-react, motion) into iframe bundle.
// Keep in sync with internal/constants.ts LOOCAL_BACKUP_STATUS_KEY.
const LOOCAL_BACKUP_STATUS_KEY = 'passport-backup-status'
import { backupToCloud, getCloudProviders } from './backup/cloud-backup'
import { backupToLocalFile } from './backup/local-backup'
import {
  backupToServer,
  backupToServerWithPasskeyKek,
  ensureServerManagedBackup,
  getServerBackupMethod,
  upgradeServerManagedBackup,
  WalletMismatchError
} from './backup/server-backup'
import type { UserProtection } from './backup/crypto-utils'
import { StorageManager, type StoredKeyshare } from './storage-manager'
import { TokenRefreshApiClient } from './token-refresh-client'
import type {
  BackupMethod,
  BackupRequest,
  BackupResult,
  BackupStatus,
  EnsureServerBackupResult,
  KeyshareBackupData,
  VaultEncryptionMethod
} from './types'

const BACKUP_VERSION = '1.0'

/** A backup's keyshare as iframe storage keeps it */
function storedKeyshare(data: KeyshareBackupData): StoredKeyshare {
  return { share: data.keyshare, ownerAddress: data.ownerAddress, sessionId: data.sessionId }
}

/**
 * Main Backup Manager class
 * Orchestrates backup operations across different methods
 */

export class BackupManager {
  private tokenClient: TokenRefreshApiClient
  /** in-flight ensureServerBackup per user and wallet: the post-DKG call and the parent's call share one run */
  private ensuring = new Map<string, Promise<EnsureServerBackupResult>>()
  private storageManager?: StorageManager

  private get keyshares(): StorageManager {
    return (this.storageManager ??= new StorageManager())
  }

  constructor() {
    this.tokenClient = new TokenRefreshApiClient()
    console.log('[iframe][BackupManager] Initialized')
  }

  /**
   * Keyshare of wallet `walletOwner` from iframe storage: the current one, or one set aside for another
   * project (another tab may have made another wallet current). Without `walletOwner`, the current one.
   */
  private getCurrentKeyshareBackupData(userId: string, walletOwner?: string): KeyshareBackupData | null {
    try {
      const stored = this.keyshares.getKeyshareOf(userId, walletOwner)
      if (!stored?.sessionId) {
        console.warn('[iframe][BackupManager] Missing keyshare data - cannot create backup', { hasKeyshare: !!stored })
        return null
      }
      return {
        userId,
        sessionId: stored.sessionId,
        keyshare: stored.share,
        ownerAddress: stored.ownerAddress as `0x${string}`,
        createdAt: Date.now(),
        version: BACKUP_VERSION
      }
    } catch (error) {
      console.error('[iframe][BackupManager] Error getting keyshare data:', error)
      return null
    }
  }

  /**
   * Get backup status for all methods
   */
  getBackupStatus(userId: string): BackupStatus {
    const statusData = localStorage.getItem(`${LOOCAL_BACKUP_STATUS_KEY}.${userId}`)

    if (statusData) {
      try {
        return JSON.parse(statusData)
      } catch (error) {
        console.error('[iframe][BackupManager] Failed to parse backup status:', error)
      }
    }

    return {
      server: { lastBackup: undefined, error: undefined },
      cloud: { lastBackup: undefined, error: undefined },
      local: { lastBackup: undefined, error: undefined }
    }
  }

  /**
   * Update backup status for a specific method
   */
  private updateBackupStatus(userId: string, method: BackupMethod, status: Partial<BackupStatus[BackupMethod]>): void {
    const currentStatus = this.getBackupStatus(userId)
    currentStatus[method] = { ...currentStatus[method], ...status }

    localStorage.setItem(`${LOOCAL_BACKUP_STATUS_KEY}.${userId}`, JSON.stringify(currentStatus))

    console.log(`[iframe][BackupManager] Updated ${method} backup status for ${userId}`)
  }

  /**
   * Create backup using specified method
   */
  /**
   * @param currentServerMethod what the vault holds, when the caller already looked it up (null = no
   * share); lets the passkey prompt come right after the user's click
   */
  async createBackup(
    userId: string,
    request: BackupRequest,
    accessToken?: string,
    projectId?: string,
    currentServerMethod?: VaultEncryptionMethod | null,
    /** Basic backups (here: the Basic → Advanced upgrade) run only on an iframe session; off unless passed */
    basicAllowed = false,
    /** owner address of the wallet of the request's project; older parents do not send it */
    walletOwner?: string
  ): Promise<BackupResult> {
    // never the request itself: it carries the backup password
    console.log('[iframe][BackupManager] Create backup request:', { method: request.method })

    const data = this.getCurrentKeyshareBackupData(userId, walletOwner)
    if (!data) {
      const error = 'No keyshare data found for backup'
      console.error('[iframe][BackupManager]', error)
      return {
        success: false,
        method: request.method,
        timestamp: Date.now(),
        error
      }
    }

    try {
      let serverOutcome: Pick<BackupResult, 'encryptionMethod' | 'upgraded'> | undefined

      switch (request.method) {
        case 'server':
          serverOutcome = await this.createServerBackup(data, request.password, accessToken, projectId, currentServerMethod, basicAllowed)
          break

        case 'local':
          await backupToLocalFile(data, request.password)
          break

        case 'cloud':
          await backupToCloud(data, request.cloudProvider, request.password)
          break

        default:
          throw new Error(`Unknown backup method: ${request.method}`)
      }

      const timestamp = Date.now()
      this.updateBackupStatus(userId, request.method, {
        lastBackup: timestamp,
        error: undefined
      })

      console.log(`[iframe][BackupManager] ✅ ${request.method} backup completed`)

      return {
        success: true,
        method: request.method,
        timestamp,
        ...serverOutcome
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Backup failed'
      console.error('[iframe][BackupManager] Backup failed:', errorMessage)

      this.updateBackupStatus(userId, request.method, {
        error: errorMessage
      })

      return {
        success: false,
        method: request.method,
        timestamp: Date.now(),
        error: errorMessage
      }
    }
  }

  /**
   * Server backup with a user-held secret (Advanced).
   * A Basic backup of this keyshare is replaced under a new DEK (Basic → Advanced); anything else is uploaded as before.
   */
  private async createServerBackup(
    data: KeyshareBackupData,
    password: string | undefined,
    accessToken?: string,
    projectId?: string,
    knownMethod?: VaultEncryptionMethod | null,
    basicAllowed = false
  ): Promise<Pick<BackupResult, 'encryptionMethod' | 'upgraded'>> {
    const current =
      knownMethod !== undefined
        ? knownMethod
        : await getServerBackupMethod(this.tokenClient, accessToken, projectId, data.ownerAddress).catch(() => null)
    const wasBasic = current === 'server-managed'

    // The passkey is asked once, first: Safari only allows it right after the user's click
    let protection: UserProtection
    if (password) {
      protection = { method: 'password', password }
    } else {
      const { deriveKEKFromPasskey } = await import('./backup/crypto-utils')
      const { kek, credentialId } = await deriveKEKFromPasskey(data.userId)
      protection = { method: 'passkey', kek, credentialId }
    }

    // etag of the share the upgrade read: the full backup replaces only that share
    let ifMatch: string | undefined
    // Without an iframe session the Basic backup cannot be read: it is replaced by a full backup, as when Basic is off
    if (wasBasic && basicAllowed) {
      const outcome = await upgradeServerManagedBackup(data, protection, this.tokenClient, accessToken, projectId)
      if (outcome.kind === 'upgraded') {
        console.log(`[iframe][BackupManager] Basic backup upgraded to ${outcome.encryptionMethod}`)
        return { encryptionMethod: outcome.encryptionMethod, upgraded: true }
      }
      console.warn(`[iframe][BackupManager] Upgrade not applicable (${outcome.reason}), uploading a new backup`)
      ifMatch = outcome.etag
    }

    if (protection.method === 'password') {
      await backupToServer(data, this.tokenClient, protection.password, accessToken, projectId, ifMatch)
    } else {
      await backupToServerWithPasskeyKek(data, protection.kek, protection.credentialId, this.tokenClient, accessToken, projectId, ifMatch)
    }
    return { encryptionMethod: protection.method, upgraded: wasBasic }
  }

  /** Encryption method of the vault backup of the wallet, null when there is none or the vault cannot tell */
  getServerBackupMethod(accessToken?: string, projectId?: string, walletOwner?: string) {
    return getServerBackupMethod(this.tokenClient, accessToken, projectId, walletOwner).catch(() => null)
  }

  /**
   * Owner address of the wallet a request is about: the server's keyshare for the request's project, as TSS
   * reports it. Not the current keyshare here (another tab may have made a sibling's current, #31), and not
   * what the parent names (an older parent names nothing): `hint` is used only when TSS does not answer.
   * undefined when neither knows; backups then use the current keyshare and restores the user's share, as before.
   */
  async resolveWalletOwner(userId: string, hint?: string, accessToken?: string, projectId?: string): Promise<string | undefined> {
    try {
      const verified = await this.tokenClient.apiCall('GET', '/api/auth/verify', undefined, projectId, accessToken)
      const owner = verified?.ownerAddress
      // a session of another user says nothing about this one's wallet
      if (verified?.userId && verified.userId !== userId) return hint
      if (typeof owner === 'string' && /^0x[0-9a-fA-F]{40}$/.test(owner)) return owner
    } catch (error) {
      console.warn('[iframe][BackupManager] TSS did not name the wallet of the project:', error instanceof Error ? error.message : String(error))
    }
    return hint
  }

  /**
   * Make sure the vault holds a backup of the wallet's keyshare, creating a Basic one when there is none.
   * Never throws and never overwrites an existing backup.
   */
  ensureServerBackup(
    userId: string,
    accessToken?: string,
    projectId?: string,
    /** false without an iframe session: an existing backup is reported, a Basic one is never created */
    basicAllowed = false,
    /** owner address of the wallet of the request's project; the current keyshare's when not given */
    walletOwner?: string
  ): Promise<EnsureServerBackupResult> {
    const owner = walletOwner ?? this.keyshares.getOwnerAddress(userId) ?? undefined
    const runKey = `${userId}:${owner?.toLowerCase() ?? ''}`
    const inFlight = this.ensuring.get(runKey)
    if (inFlight) return inFlight

    const run = ensureServerManagedBackup(this.getCurrentKeyshareBackupData(userId, owner), this.tokenClient, accessToken, projectId, {
      allowCreate: basicAllowed,
      owner
    })
      .then(async (result) => {
        if (result.status !== 'failed') this.updateBackupStatus(userId, 'server', { lastBackup: Date.now(), error: undefined })
        console.log('[iframe][BackupManager] ensureServerBackup:', result.status, result.status === 'failed' ? result.reason : result.encryptionMethod)
        return result
      })
      .catch((error): EnsureServerBackupResult => ({
        status: 'failed',
        reason: 'unknown',
        error: error instanceof Error ? error.message : String(error)
      }))
      .finally(() => this.ensuring.delete(runKey))

    this.ensuring.set(runKey, run)
    return run
  }

  /**
   * Restore keyshare from server backup
   */
  async restoreFromServer(
    userId: string,
    password?: string,
    accessToken?: string,
    projectId?: string,
    /** a Basic backup opens only on an iframe session */
    basicAllowed = false,
    /** owner address of the wallet to restore (the server's keyshare for the request's project) */
    walletOwner?: string
  ): Promise<KeyshareBackupData> {
    console.log('[iframe][BackupManager] Restore from server request for userId:', userId)

    try {
      const { restoreFromServer } = await import('./backup/server-backup')
      const backupData = await restoreFromServer(userId, this.tokenClient, password, accessToken, projectId, basicAllowed, walletOwner)

      console.log('[iframe][BackupManager] ✅ Server restore completed')

      // A keyshare of another wallet that is current here is set aside, never overwritten
      this.keyshares.installKeyshare(userId, storedKeyshare(backupData))

      console.log('[iframe][BackupManager] Keyshare saved to localStorage')

      return backupData
    } catch (error: any) {
      // The backup holds another wallet of this user: keep that keyshare aside for its own project
      if (error instanceof WalletMismatchError) this.keyshares.keepAside(userId, storedKeyshare(error.backup))
      const errorMessage = error.message || 'Restore failed'
      console.error('[iframe][BackupManager] Restore failed:', errorMessage)
      throw error
    }
  }

  /**
   * Encrypt backup data without uploading (for cloud/local backups)
   * Returns encrypted data that parent can upload/download
   */
  async encryptBackupData(userId: string, password?: string, walletOwner?: string): Promise<any> {
    console.log('[iframe][BackupManager] Encrypting backup data for userId:', userId)

    // the keyshare of the wallet of the request's project, not of whatever wallet another tab made current
    const data = this.getCurrentKeyshareBackupData(userId, walletOwner)
    if (!data) {
      throw new Error('No keyshare data found for backup')
    }

    // Import encryption function
    const { encryptKeyshare, deriveBackupPasswordFromPasskey } = await import('./backup/crypto-utils')

    let encryptionPassword = password
    let credentialId: string | undefined

    if (!encryptionPassword) {
      // Use passkey-derived password
      const result = await deriveBackupPasswordFromPasskey(userId)
      encryptionPassword = result.password
      credentialId = result.credentialId
    }

    const encrypted = await encryptKeyshare(data, encryptionPassword, password ? 'password' : 'passkey', credentialId)

    console.log('[iframe][BackupManager] ✅ Backup data encrypted')

    // encryptKeyshare already includes all necessary fields
    return encrypted
  }

  /**
   * Encrypt current keyshare as EnvelopeEncryptedShare (server/guardian format).
   * Returns JSON string of the envelope.
   */
  async encryptEnvelopeData(userId: string, password: string, walletOwner?: string): Promise<string> {
    const data = this.getCurrentKeyshareBackupData(userId, walletOwner)
    if (!data) {
      throw new Error('No keyshare found for backup')
    }

    const { envelopeEncryptKeyshareWithPassword } = await import('./backup/crypto-utils')
    const envelope = await envelopeEncryptKeyshareWithPassword(data, password)
    return JSON.stringify(envelope)
  }

  /**
   * Decrypt an EnvelopeEncryptedShare and restore keyshare to localStorage.
   */
  async decryptAndRestoreEnvelope(
    envelopeJson: string,
    password: string,
    userId: string,
  ): Promise<void> {
    const { envelopeDecryptKeyshareWithPassword } = await import('./backup/crypto-utils')
    const envelope = JSON.parse(envelopeJson)
    const backupData = await envelopeDecryptKeyshareWithPassword(envelope, password)

    // Same as server restore: a current keyshare of another wallet is set aside, never overwritten
    this.keyshares.installKeyshare(userId, storedKeyshare(backupData))
  }

  /**
   * Restore keyshare from local file backup
   */
  async restoreFromLocalFile(fileContent: string, userId: string, password?: string): Promise<KeyshareBackupData> {
    console.log('[iframe][BackupManager] Restore from local file request for userId:', userId)

    try {
      const { restoreFromLocalFile } = await import('./backup/local-backup')
      const backupData = await restoreFromLocalFile(fileContent, userId, password)

      console.log('[iframe][BackupManager] ✅ Local file restore completed')

      // A current keyshare of another wallet is set aside, never overwritten
      this.keyshares.installKeyshare(userId, storedKeyshare(backupData))

      console.log('[iframe][BackupManager] Keyshare saved to localStorage')

      return backupData
    } catch (error: any) {
      const errorMessage = error.message || 'Local file restore failed'
      console.error('[iframe][BackupManager] Local file restore failed:', errorMessage)
      throw error
    }
  }

  /**
   * Get available cloud providers
   */
  getAvailableCloudProviders() {
    return getCloudProviders()
  }
}
